import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { supabase } from "@/services/supabase";
import { useSessionStore } from "./sessionStore";
import { useCompanyUserStore } from "./companyUserStore";

interface Permission {
    key: string;
    name: string;
    category: string;
}

interface PermissionsStore {
    // State
    permissions: Permission[];
    permissionKeys: string[];
    isPermissionsLoading: boolean;
    permissionsError: string | null;

    // Actions
    fetchPermissions: () => Promise<void>;
    clearPermissionsData: () => void;
    setPermissions: (permissions: Permission[]) => void;

    // Computed getters
    hasPermission: (permissionKey: string) => boolean;
    checkPermissions: (
        permissions: string[],
        operator?: "AND" | "OR",
    ) => boolean;
}

export const usePermissionsStore = create<PermissionsStore>()(
    devtools(
        (set, get) => ({
            // Initial state
            permissions: [],
            permissionKeys: [],
            isPermissionsLoading: false,
            permissionsError: null,

            // Actions
            fetchPermissions: async () => {
                const session = useSessionStore.getState().session;
                const companyUser = useCompanyUserStore.getState().companyUser;

                if (!session?.user || !companyUser) {
                    console.log(
                        "[permissionsStore] fetchPermissions: no session or company user",
                    );
                    set(
                        { permissionsError: "No session or company user" },
                        false,
                        "fetchPermissions/noAuth",
                    );
                    return;
                }

                // Prevent duplicate calls
                const currentState = get();
                if (currentState.isPermissionsLoading) {
                    console.log(
                        "[permissionsStore] fetchPermissions: already loading, skipping",
                    );
                    return;
                }

                set(
                    { isPermissionsLoading: true, permissionsError: null },
                    false,
                    "fetchPermissions/start",
                );
                console.log("[permissionsStore] fetchPermissions: start");

                try {
                    const { data, error } = await supabase.rpc(
                        "get_user_permissions",
                    );
                    if (error) throw error;

                    const mappedPermissions: Permission[] = (data ?? []).map((
                        row: any,
                    ) => ({
                        key: row.permission_key,
                        name: row.permission_name,
                        category: row.category ?? "Uncategorized",
                    }));

                    // Deduplicate by key just in case
                    const uniquePermissions = mappedPermissions.filter((
                        perm,
                        index,
                        self,
                    ) => index === self.findIndex((p) => p.key === perm.key));
                    const permissionKeys = uniquePermissions.map((p) => p.key);

                    set(
                        {
                            permissions: uniquePermissions,
                            permissionKeys,
                            isPermissionsLoading: false,
                            permissionsError: null,
                        },
                        false,
                        "fetchPermissions/success",
                    );
                    console.log(
                        "[permissionsStore] fetchPermissions: success",
                        {
                            permissions: uniquePermissions,
                            permissionKeys,
                        },
                    );
                } catch (error) {
                    const errorMessage = error instanceof Error
                        ? error.message
                        : "Unknown error";
                    set(
                        {
                            permissions: [],
                            permissionKeys: [],
                            isPermissionsLoading: false,
                            permissionsError: errorMessage,
                        },
                        false,
                        "fetchPermissions/error",
                    );
                    console.error("Error fetching permissions:", error);
                }
            },

            setPermissions: (permissions) => {
                const permissionKeys = permissions.map((p) => p.key);
                set(
                    { permissions, permissionKeys },
                    false,
                    "setPermissions",
                );
                console.log("[permissionsStore] setPermissions", {
                    permissions,
                    permissionKeys,
                });
            },

            clearPermissionsData: () => {
                set(
                    {
                        permissions: [],
                        permissionKeys: [],
                        isPermissionsLoading: false,
                        permissionsError: null,
                    },
                    false,
                    "clearPermissionsData",
                );
                console.log(
                    "[permissionsStore] clearPermissionsData: store reset",
                );
            },

            // Computed getters
            hasPermission: (permissionKey: string) => {
                const { permissionKeys, isPermissionsLoading } = get();
                const result = !isPermissionsLoading &&
                    permissionKeys.includes(permissionKey);
                console.log(
                    `{permissionsStore} hasPermission("${permissionKey}") ->`,
                    result,
                );
                return result;
            },
            checkPermissions: (
                requiredPermissions: string[],
                operator: "AND" | "OR" = "AND",
            ) => {
                const { hasPermission, isPermissionsLoading } = get();
                if (isPermissionsLoading) return false;

                if (operator === "OR") {
                    const result = requiredPermissions.some((permission) =>
                        hasPermission(permission)
                    );
                    console.log("[permissionsStore] checkPermissions OR", {
                        requiredPermissions,
                        result,
                    });
                    return result;
                }
                const result = requiredPermissions.every((permission) =>
                    hasPermission(permission)
                );
                console.log("[permissionsStore] checkPermissions AND", {
                    requiredPermissions,
                    result,
                });
                return result;
            },
        }),
        {
            name: "permissions-store",
        },
    ),
);

// Selector hooks
export const usePermissions = () =>
    usePermissionsStore((state) => state.permissions);
export const usePermissionKeys = () =>
    usePermissionsStore((state) => state.permissionKeys);
export const usePermissionsLoading = () =>
    usePermissionsStore((state) => state.isPermissionsLoading);
export const useHasPermission = (permission: string) =>
    usePermissionsStore((state) => state.hasPermission(permission));
export const useCheckPermissions = (
    permissions: string[],
    operator: "AND" | "OR" = "AND",
) => usePermissionsStore((state) =>
    state.checkPermissions(permissions, operator)
);
export const useFetchPermissions = () =>
    usePermissionsStore((state) => state.fetchPermissions);
export const useSetPermissions = () =>
    usePermissionsStore((state) => state.setPermissions);
